import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { adminGetOrders, adminSetOrderStatus, adminBulkStatus } from "@/lib/api";

const STATUSES = [
  { id: "preparing", label: "Preparing Order" },
  { id: "shipped", label: "Order Shipped" },
  { id: "in_transit", label: "Order in Transit" },
  { id: "out_for_delivery", label: "Out for Delivery" },
  { id: "ready_for_pickup", label: "Ready for Pick Up" },
];

const badgeCls = (s) =>
  ({
    preparing: "bg-[#FFB56B]/20 text-[#9A5A12]",
    shipped: "bg-[#7BC5D8]/20 text-[#1F6B80]",
    in_transit: "bg-[#B6A6E0]/25 text-[#4B3B8C]",
    out_for_delivery: "bg-[#FF8473]/20 text-[#B3402F]",
    ready_for_pickup: "bg-[#D7E85C]/30 text-[#5C6B12]",
  }[s] || "bg-[#1F3864]/10 text-[#1F3864]");

export default function OrdersAdmin() {
  const [orders, setOrders] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("preparing");
  const [filter, setFilter] = useState("");

  const load = () => adminGetOrders(filter || undefined).then(setOrders).catch(() => toast.error("Failed to load orders"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const toggle = (id) =>
    setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const setStatus = async (id, status) => {
    try {
      await adminSetOrderStatus(id, status);
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  const applyBulk = async () => {
    if (selected.size === 0) return;
    try {
      const r = await adminBulkStatus([...selected], bulkStatus);
      toast.success(`${r.updated} order(s) updated`);
      setSelected(new Set());
      load();
    } catch { toast.error("Bulk update failed"); }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Orders</h1>
          <p className="text-[#1F3864]/60">Update fulfillment status — customers see this on their dashboard.</p>
        </div>
        <select data-testid="orders-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="border border-[#1F3864]/20 rounded-full px-4 py-2 text-sm font-bold text-[#1F3864] bg-white">
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      {/* Bulk bar */}
      <div data-testid="bulk-bar" className="flex flex-wrap items-center gap-3 bg-white rounded-2xl border border-[#1F3864]/10 p-4 mb-6">
        <span className="text-sm font-bold text-[#1F3864]">{selected.size} selected</span>
        <select data-testid="bulk-status-select" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}
          className="border border-[#1F3864]/20 rounded-lg px-3 py-2 text-sm bg-white">
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        <button data-testid="bulk-apply-btn" onClick={applyBulk} disabled={selected.size === 0}
          className="bg-[#1F3864] text-[#D7E85C] font-bold text-sm px-4 py-2 rounded-full disabled:opacity-40">
          Apply to selected
        </button>
        {selected.size > 0 && (
          <button onClick={() => setSelected(new Set())} className="text-sm text-[#FF8473] font-bold">Clear</button>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-[#1F3864]/10 overflow-hidden">
        {orders.length === 0 ? (
          <p data-testid="orders-empty" className="text-center text-[#1F3864]/50 text-sm py-12">No orders yet. Paid checkouts will appear here.</p>
        ) : (
          <div className="divide-y divide-[#1F3864]/10">
            {orders.map((o) => (
              <div key={o.id} data-testid={`order-row-${o.id}`} className="p-4 flex flex-wrap items-center gap-4">
                <input type="checkbox" data-testid={`order-check-${o.id}`} checked={selected.has(o.id)} onChange={() => toggle(o.id)}
                  className="accent-[#1F3864] h-4 w-4" />
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold text-[#1F3864]">{o.package_name || "Order"} · {o.currency === "php" ? "₱" : "$"}{o.amount}</div>
                  <div className="text-xs text-[#1F3864]/50">
                    {o.customer_email || o.metadata?.email || "guest"} · {new Date(o.created_at).toLocaleDateString()}
                    {o.shipping_method === "pickup" && o.pickup_location_name && <> · Pickup: {o.pickup_location_name}</>}
                  </div>
                </div>
                <span data-testid={`order-status-${o.id}`} className={`px-3 py-1.5 rounded-full text-xs font-bold ${badgeCls(o.status)}`}>
                  {o.status_label || o.status}
                </span>
                <div className="relative">
                  <select
                    data-testid={`order-status-select-${o.id}`}
                    value={o.status}
                    onChange={(e) => setStatus(o.id, e.target.value)}
                    className="appearance-none border border-[#1F3864]/20 rounded-lg pl-3 pr-8 py-2 text-xs font-bold text-[#1F3864] bg-white"
                  >
                    {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#1F3864]/50 pointer-events-none" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
